import * as React from 'react';
import { SheetContent } from '@/components/ui/sheet';
import { cn } from '@/lib/utils';

/**
 * Owner-scoped variant of `SheetContent`. Neutralises the legacy neobrutalist
 * thick border inherited by the shared shadcn primitive (used by marketing/
 * admin), applies the `/owner/*` soft-shadow + slate-200 border language.
 *
 * Use this for ANY Sheet rendered inside `[data-app-shell="owner"]` (mobile
 * rail drawer, contextual drawers, etc.).
 *
 * The shadcn base sheet applies `border-r-[length:var(--border-brutal)]`
 * (~4px) + `border-charcoal-950` (near-black) when `side="left"`. We override
 * those with `border-r border-slate-200` (1px subtle) + a soft horizontal
 * shadow on the right edge.
 */

type OwnerSheetContentProps = React.ComponentProps<typeof SheetContent>;

// Per-side overrides — only the edge that faces the content gets a soft 1px
// slate-200 border (instead of the ~4px charcoal-950 brutalist default from
// the shared shadcn primitive). `!` suffix = Tailwind 4 important modifier.
const SIDE_OVERRIDE: Record<NonNullable<OwnerSheetContentProps['side']>, string> = {
  left: 'border-r-[length:1px]! border-r-slate-200! dark:border-r-charcoal-700!',
  right: 'border-l-[length:1px]! border-l-slate-200! dark:border-l-charcoal-700!',
  top: 'border-b-[length:1px]! border-b-slate-200! dark:border-b-charcoal-700!',
  bottom: 'border-t-[length:1px]! border-t-slate-200! dark:border-t-charcoal-700!',
};

export function OwnerSheetContent({ className, side = 'left', ...props }: OwnerSheetContentProps) {
  return (
    <SheetContent
      side={side}
      className={cn(
        SIDE_OVERRIDE[side],
        // Replace harsh black shadow with soft elevation
        'shadow-[0_12px_32px_-16px_rgba(15,23,42,0.20),0_2px_4px_-2px_rgba(15,23,42,0.04)]!',
        'bg-white dark:bg-charcoal-900',
        className,
      )}
      {...props}
    />
  );
}
